import React, { useState, useRef, useCallback, useContext } from "react";
import * as Yup from "yup";
import { CustomError } from "../../../utils/customeError";
import { ErrorCallback, FolderName, ProgressCallback, SKMediaType, SuccessCallback, uploadFile } from "../../../api/shared/uploadService";
import { UploadTask } from "firebase/storage";
import { CreateArtifactContext } from "../../../context/CreateArtifactContext";
import { ContentState } from "../../../models/SKArtifact";
import { isEditable } from "@testing-library/user-event/dist/utils";

interface ImageInputProps {
  index?: number;
  onFileChange: (index: number, url: string | null) => void;
  imageUrl?: string;
  maxFileSize?: number;
  docId: string;
  editable?: boolean;
  folder: FolderName;
  imageWidth?: number;
  imageHeight?: number;
  fileTypeShow?: string;
}

const ImageInput: React.FC<ImageInputProps> = ({
  index = 0,
  docId,
  onFileChange,
  imageUrl,
  maxFileSize = 2,
  editable = true,
  folder,
  imageWidth = 150,
  imageHeight = 200,
  fileTypeShow = "JPG, JPEG, PNG, HEIF, or GIF",
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageTask, setImageTask] = useState<UploadTask | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [calculatedHeight, setCalculatedHeight] = useState<number>(imageHeight);

  const progressCallback: ProgressCallback = (progress: number, mediaType: SKMediaType) => {
    console.log(`Upload progress: ${progress}%`);
  };

  const errorCallback: ErrorCallback = (error: Error, mediaType: SKMediaType) => {
    console.error(`Upload failed: ${error.message}`);
    setUploadingPhoto(false);
  };

  const fileChange = useCallback(
    (url: string) => {
      onFileChange(index, url);
    },
    [index, onFileChange]
  );

  const successCallback: SuccessCallback = (url: string, mediaType: SKMediaType) => {
    setUploadingPhoto(false);
    fileChange(url);
  };

  const schema = Yup.object().shape({
    imageFile: Yup.mixed<File>().test("fileSize", "The file is too large", value => {
      return value && value.size <= maxFileSize * 1024 * 1024;
    }),
  });

  const validateData = async (file: File) => {
    try {
      await schema.validate({ imageFile: file }, { abortEarly: false });
    } catch (error) {
      throw new CustomError(`File size should be below ${maxFileSize}MB`);
    }
  };

  const setImageHeight = (naturalWidth: number, naturalHeight: number) => {
    const aspectRatio = naturalWidth / naturalHeight;
    const adjustedHeight = imageWidth / aspectRatio;
    setCalculatedHeight(adjustedHeight);
  };

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
        const fileUrl = URL.createObjectURL(file);
        await validateData(file);
        setErrorMessage("");
        setImagePreview(fileUrl);
        setUploadingPhoto(true);
        const img = new Image();
        img.src = fileUrl;

        img.onload = () => {
          const naturalWidth = img.naturalWidth;
          const naturalHeight = img.naturalHeight;
          setImageHeight(naturalWidth, naturalHeight);
          let task = uploadFile(file, docId, SKMediaType.photo, progressCallback, errorCallback, successCallback, folder);
          setImageTask(task);
        };
      } catch (err) {
        setErrorMessage(`File size should be below ${maxFileSize}MB`);
      }
    },
    [successCallback, docId, maxFileSize, imageWidth]
  );

  const removeImage = () => {
    onFileChange(index, null);
    setImagePreview(null);
    setCalculatedHeight(imageHeight);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div
      id="container"
      className={`${imageUrl ? "h-max " : "h-[" + imageHeight + "px]"} flex bg-white border rounded-[12px] border-[#D1D1D6]`}
      style={imageUrl ? { width: `${imageWidth}px` } : { width: `${imageWidth}px`, height: `${imageHeight}px` }}
      // onClick={() => {
      //   if (editable) fileInputRef?.current?.click();
      // }}
    >
      <div
        className="relative flex items-center justify-center w-full" // Ensure this container has relative positioning
      >
        {!uploadingPhoto && editable && imageUrl && (
          <button
            id="close-button"
            className="absolute top-2 right-1"
            onClick={e => {
              e.stopPropagation();
              removeImage();
            }}
          >
            <img className="w-[20px] h-[20px]" src="/icons/CloseButton.png" alt="remove" />
          </button>
        )}
        <input
          key={index}
          name="imageFile"
          type="file"
          ref={fileInputRef}
          accept=".jpg,.jpeg,.png,.heif,.gif"
          onChange={handleFileChange}
          className="hidden"
          id={`file-upload-${index}`}
          disabled={!editable}
        />

        {uploadingPhoto && (
          <div className="flex flex-col items-center justify-center p-4 space-y-4 bg-white rounded-2xl">
            <img src="/icons/mediaupload.gif" alt="Saving" className="w-[25px] h-[25px]" />
            <div className="font-bold text-center text-gray-400 w-[69px] h-[16px]">Uploading...</div>
            <button
              className="text-black border border-gray-400 rounded-md w-[53px] h-[30px]"
              onClick={e => {
                e.stopPropagation();
                setImagePreview(null);
                setUploadingPhoto(false);
                imageTask && imageTask.cancel();
              }}
            >
              Cancel
            </button>
          </div>
        )}

        {!uploadingPhoto && imageUrl && (
          <div>
            <img
              src={imageUrl}
              alt={`photo_${index}`}
              className="object-cover w-full h-full p-[1px]"
              style={{
                width: `${imageWidth}px`,
                height: `${calculatedHeight}px`,
                overflow: "hidden",
                borderRadius: "12px",
                // borderColor: "#D1D1D6",
                // borderWidth: "2px",
              }}
              onLoad={e => {
                const imgElement = e.target as HTMLImageElement;
                setImageHeight(imgElement.naturalWidth, imgElement.naturalHeight);
              }}
            />
          </div>
        )}

        {!(uploadingPhoto || imageUrl) && (
          <label htmlFor={`file-upload-${index}`} className="cursor-pointer">
            <div className="flex flex-col items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-[27px] h-[30px] mb-[5px] text-[#8E8E93]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
              </svg>
              <span className="image-input-media-type">{fileTypeShow}</span>
            </div>
          </label>
        )}
      </div>
    </div>
  );
};

export default ImageInput;
